import { type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { ChevronIcon } from "vega-icons";

import { useStore } from "@nanostores/react";
import { ActionMenu } from "@vega-core/components/ActionMenu/ActionMenu";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { $pendingActionsBanner } from "@vega-core/stores/pendingActionsBanner.store";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { NavbarSkeleton } from "./Navbar.skeleton";
import { NavbarBanner } from "./NavbarBanner";

export type NavbarItem = {
  label: string;
  onClick?: () => void;
  icon?: ReactNode;
  href?: string;
};

type UserInfo = {
  name: string;
  initials: string;
  avatarImage?: string;
};

export type NavbarProps = {
  items?: NavbarItem[];
  userMenuItems?: NavbarItem[];
  user?: UserInfo;
  className?: string;
  isLoading?: boolean;
  customNode?: ReactNode;
};

export const Navbar = ({
  items = [],
  userMenuItems = [],
  user,
  className,
  isLoading,
  customNode,
}: NavbarProps) => {
  const navigate = useNavigate();
  const bannerContent = useStore($pendingActionsBanner);
  const hasBanner = Boolean(bannerContent);

  const renderButtonWrapper = ({ href, label, icon, onClick }: NavbarItem) => {
    const button = (
      <ThemedButton
        aria-label={label}
        className="text-text/secondary hover:text-text/primary"
        dataTestId={`${label}-button`}
        onClick={onClick}
        variant="tertiary"
      >
        {icon}
      </ThemedButton>
    );

    return isTruthy(href) ? (
      <Link key={label} to={href}>
        {button}
      </Link>
    ) : (
      <div key={label}>{button}</div>
    );
  };

  const navbarWithBannerHeight =
    "h-[calc(theme(height.navbar)+theme(height.navbar-banner))]";

  return (
    <nav
      className={cnTwMerge(
        "hidden md:flex fixed w-full z-navbar border-b border-border/primary bg-background/primary flex-col",
        hasBanner ? navbarWithBannerHeight : "h-navbar",
        className,
      )}
    >
      <div className="h-navbar flex items-center justify-end px-4  sm:px-4 md:px-8 xl:px-[52px]">
        {isLoading ? (
          <NavbarSkeleton itemCount={items.length} showUser />
        ) : (
          <div className="flex items-center gap-4">
            {customNode}
            {items.map((item) => (
              <Tooltip
                content={item.label}
                key={item.label}
                providerProps={{ delayDuration: 0 }}
              >
                {renderButtonWrapper(item)}
              </Tooltip>
            ))}

            {user ? (
              <>
                {items.length > 0 && (
                  <div
                    className="h-6 w-px bg-border/primary mx-2"
                    data-testid="navbar-divider"
                  />
                )}
                <ActionMenu
                  contentClassName="w-64 border-border/secondary bg-background/card"
                  dataTestId="navbar-user-menu"
                  menuItems={userMenuItems.map((item) => ({
                    title: item.label,
                    icon: item.icon,
                    onClick: () => {
                      if (item.onClick) {
                        item.onClick();
                      } else if (item.href) {
                        navigate(item.href);
                      }
                    },
                    className:
                      "flex items-center gap-2 bg-background/card hover:bg-background/secondary",
                  }))}
                  renderTrigger={({ isOpen }) => (
                    <div
                      className="flex items-center gap-2 cursor-pointer rounded-md outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default px-0.5"
                      tabIndex={0}
                    >
                      <Avatar initials={user.initials} />
                      <span className="body-base-medium text-text/primary">
                        {user.name}
                      </span>
                      <ChevronIcon
                        className={cnTwMerge(
                          "ml-4 h-4 w-4 text-text/secondary transition-transform duration-200",
                          isOpen ? "rotate-180" : "",
                        )}
                      />
                    </div>
                  )}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
      {bannerContent ? <NavbarBanner>{bannerContent}</NavbarBanner> : null}
    </nav>
  );
};
